import { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../../utils/modal/ModalProvider";
import styles from "../../../utils/modal/DetailModal.module.css";
import modalStyles from "./ServiceUserDetailModal.module.css";
import { IAM_SERVER_UNIFIEDPROFILE, IAM_SERVER_SERVICE_USER, IAM_SERVER_SERVICE_AUTH } from "../../../utils/Config";
import axios from "../../../utils/api/axios";
import Switch from "@mui/material/Switch";
import { InnocodeBadge, InnocodeButton, InnocodeChip, InnocodeDropdown, useDialog, useInnocodeLoadingOverlay } from "@intsoft/innocode-core";
import dayjs from "dayjs";

/**
 * 사용자 상세정보 모달 Container
 * @param data
 * @param update
 * @returns {JSX.Element}
 * @constructor
 */
export const ServiceUserDetailModal = ({ data, update, serviceId }) => {

    const { closeModal } = useContext(ModalContext);
    const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
    const { showDialog } = useDialog();

    const [checked, setChecked] = useState(false); // 비밀번호 변경 옵션(Switch)

    const [userData, setUserData] = useState({
        unifiedProfileId: '',
        name: '',
    });

    const [serviceUser, setServiceUser] = useState({});
    const [serviceAuthList, setServiceAuthList] = useState([]);

    const [selectedAuths, setSelectedAuths] = useState([]);
    const [allAuthList, setAllAuthList] = useState([]);

    const fetchData = async () => {
        showLoading();
        try {
            // ✨ Promise.all을 사용하여 여러 API를 병렬로 호출 (성능 향상)
            const [userRes, serviceUserRes, authRes] = await Promise.all([
                axios.get(IAM_SERVER_UNIFIEDPROFILE + "/get/" + data.userId),
                axios.get(IAM_SERVER_SERVICE_USER + "/get/" + data.serviceUserId),
                axios.post(IAM_SERVER_SERVICE_AUTH + '/all', {
                    sortField: ['createdDate'],
                    sortOrder: ['desc'],
                    operator: "AND",
                    conditions: [{ field: "serviceId", operator: "EQUALS", value: serviceId }]
                })
            ]);

            // 사용자 기본 정보 설정
            setUserData({
                unifiedProfileId: userRes.data.data.unifiedProfileId,
                name: userRes.data.data.name,
            });

            // 서비스 사용자 정보 설정
            const fetchedServiceUser = serviceUserRes.data.data;
            const originalAuths = authRes.data.data.data || [];
            const authsWithServiceAdmin = originalAuths.some(auth => auth.authorityEngName === 'SYS_ADMIN')
                ? originalAuths
                : [...originalAuths, {
                    authorityEngName: 'SYS_ADMIN',
                    authorityName: '서비스 관리자',
                    authorityType: 'SERVICE',
                    serviceId
                }];

            setAllAuthList(authsWithServiceAdmin);

            const membershipType = fetchedServiceUser.membershipType;
            let filteredAuthList = [];

            // 2. membershipType에 따라 authList를 필터링합니다.
            if (membershipType === 'TENANT') {
                // TENANT 멤버십은 'TENANT' 타입 권한 + 'SERVICE' 타입 권한을 가질 수 있음
                // 단, TENANT_OWNER는 목록에서 제외 (선택 불가)
                filteredAuthList = authsWithServiceAdmin.filter(auth =>
                    (auth.authorityType === 'TENANT' || auth.authorityType === 'SERVICE' || auth.authorityEngName === 'SYS_ADMIN')
                    && auth.authorityEngName !== 'TENANT_OWNER'
                );
            } else { // 'PROFILE', 'EMAIL' 등
                // 그 외 멤버십은 'TENANT' 타입 권한을 가질 수 없음
                filteredAuthList = authsWithServiceAdmin.filter(auth =>
                    auth.authorityType !== 'TENANT' || auth.authorityEngName === 'SYS_ADMIN'
                );
            }

            setServiceUser(fetchedServiceUser);
            setServiceAuthList(filteredAuthList);

            if (fetchedServiceUser && fetchedServiceUser.authorityIds) {
                setSelectedAuths(fetchedServiceUser.authorityIds);
            }

        } catch (e) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "실패",
                message: "상세 정보 조회에 실패했습니다.",
                confirmText: "확인",
            });
            closeModal();
        } finally {
            hideLoading();
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAuthChange = (authEngName) => {
        // 이미 추가된 권한은 다시 추가하지 않음
        if (!selectedAuths.includes(authEngName)) {
            setSelectedAuths(prevAuths => [...prevAuths, authEngName]);
        }
    };

    const handleWithdrawal = (item) => {
        showDialog({
            iconType: "warning",
            type: 'delete',
            title: "서비스 탈퇴",
            message: "서비스를 탈퇴 하시겠습니까?\n탈퇴 시 서비스의 모든 정보가 삭제됩니다.",
            confirmText: "탈퇴",
            cancelText: "취소",
            onConfirm: () => {
                const url = `${IAM_SERVER_SERVICE_USER}/delete/${item.id}`;

                axios.post(url)
                    .then((res) => {
                        update();
                        showDialog({
                            iconType: "success",
                            type: 'success',
                            title: "완료",
                            message: "서비스가 성공적으로 탈퇴되었습니다.",
                            confirmText: "확인",
                        });
                    })
                    .catch((e) => {
                        console.error("서비스 탈퇴 실패:", e);
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "오류",
                            message: "서비스 탈퇴 중 오류가 발생했습니다.",
                            confirmText: "확인",
                        });
                    });
            },
        });
    };

    const handleToggleStatus = (event) => {
        const newStatus = event.target.checked;
        const confirmText = newStatus ? "사용자를 활성화 하시겠습니까?" : "사용자를 비활성화 하시겠습니까?";

        showDialog({
            iconType: "warning",
            type: 'confirm',
            title: "상태 변경 확인",
            message: confirmText,
            confirmText: "예",
            cancelText: "아니요",
            onConfirm: () => {
                const url = `${IAM_SERVER_SERVICE_USER}/update/status`;
                const payload = {
                    profileId: serviceUser.profileId,
                    serviceId: serviceUser.serviceId,
                    status: newStatus
                };
                axios.post(url, payload)
                    .then((res) => {
                        setServiceUser(prev => ({ ...prev, status: res.data.data.status }));
                        showDialog({
                            iconType: "success",
                            type: 'success',
                            title: "완료",
                            message: "상태가 변경되었습니다.",
                            confirmText: "확인",
                        });
                    })
                    .catch((e) => {
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "오류",
                            message: "상태 변경 중 오류가 발생했습니다.",
                            confirmText: "확인",
                        });
                    });
            },
        });
    };

    // const handleToggleStatus = (item) => {
    //     const updatedStatus = !item.status;

    //     const confirmText = updatedStatus ? "활성화 하시겠습니까?" : "비활성화 하시겠습니까?";

    //     showDialog(DialogEnum.CONFIRM, "상태 변경 확인", confirmText, () => {
    //         const url = `${IAM_SERVER_SERVICE_USER}/update/status`;

    //         const data = {
    //             profileId: item.profileId,
    //             serviceId: item.serviceId,
    //             status: updatedStatus
    //         };

    //         axios.post(url, data)
    //             .then((res) => {

    //                 setServiceUser(prev => ({
    //                     ...prev,
    //                     status: res.data.data.status
    //                 }));

    //                 // setServiceUser(res.data);

    //                 showDialog(DialogEnum.SUCCESS, "완료", "상태가 변경되었습니다.");
    //             })
    //             .catch((e) => {
    //                 console.error("상태 변경 실패:", e);
    //                 showDialog(DialogEnum.DEFAULT, "오류", "상태 변경 중 오류가 발생했습니다.");
    //             });
    //     });
    // };

    const handleModify = () => {

        if (selectedAuths.length === 0) {
            showDialog({
                iconType: "warning",
                type: 'error',
                title: "입력 오류",
                message: "사용자는 최소 1개 이상의 권한을 가져야 합니다.",
                confirmText: "확인",
            });
            return;
        }
        // 기존 권한과 선택된 권한이 같은지 확인
        const originalAuths = [...(serviceUser?.authorityIds || [])].sort();
        const newAuths = [...selectedAuths].sort();

        // 배열이 완전히 동일한지 확인
        const isUnchanged = originalAuths.length === newAuths.length && originalAuths.every((value, index) => value === newAuths[index]);

        if (isUnchanged) {
            showDialog({
                iconType: "info",
                type: 'info',
                title: "알림",
                message: "변경된 내용이 없습니다.",
                confirmText: "확인",
            });
            return;
        }

        showDialog({
            iconType: "warning",
            type: 'confirm',
            title: "권한 수정",
            message: "사용자의 권한을 수정하시겠습니까?",
            confirmText: "예",
            cancelText: "아니요",
            onConfirm: () => {
                // 백엔드의 권한 수정 API 엔드포인트가 필요합니다. (아래는 예시)
                const url = `${IAM_SERVER_SERVICE_USER}/update/roles`;
                const payload = {
                    profileId: serviceUser.profileId,
                    serviceId: serviceUser.serviceId,
                    authorityIds: newAuths
                };

                axios.post(url, payload)
                    .then(res => {
                        showDialog({
                            iconType: "success",
                            type: 'success',
                            title: "성공",
                            message: "권한이 수정되었습니다.",
                            confirmText: "확인",
                            onConfirm: () => {
                                update(); // 부모 컴포넌트의 데이터 목록 새로고침
                                closeModal();
                            },
                        });
                    })
                    .catch(err => {
                        showDialog({
                            iconType: "warning",
                            type: 'error',
                            title: "실패",
                            message: err.response?.data?.message || "권한 수정 실패",
                            confirmText: "확인",
                        });
                    });
            },
        });
    }

    const handleAddAuth = (authEngName) => {
        // 이미 추가된 권한은 다시 추가하지 않음
        if (!selectedAuths.includes(authEngName)) {
            setSelectedAuths(prevAuths => [...prevAuths, authEngName]);
        }
    };

    const handleRemoveAuth = (authEngNameToRemove) => {
        // 'TENANT_OWNER'는 제거할 수 없음
        if (authEngNameToRemove === 'TENANT_OWNER') {
            showDialog({
                iconType: "info",
                type: 'info',
                title: "알림",
                message: "테넌트 오너 권한은 제거할 수 없습니다.",
                confirmText: "확인",
            });
            return;
        }
        // 권한이 1개만 남았으면 제거할 수 없음
        if (selectedAuths.length <= 1) {
            showDialog({
                iconType: "info",
                type: 'info',
                title: "알림",
                message: "사용자는 최소 1개 이상의 권한을 가져야 합니다.",
                confirmText: "확인",
            });
            return;
        }
        setSelectedAuths(prevAuths => prevAuths.filter(auth => auth !== authEngNameToRemove));
    };

    const availableAuthsToSelect = serviceAuthList.filter(
        auth => !selectedAuths.includes(auth.authorityEngName)
    );

    return (
        <div style={{ overflow: "auto" }}>
            <div>
                <ul className={styles.layer__listWrap}>
                    <li className={styles.layer__list}>
                        <p className={styles.layer__listTit}>아이디</p>
                        <input className={styles.layer__listInput}
                            type={"text"}
                            name={"id"}
                            value={userData.unifiedProfileId}
                            readOnly={true} />
                    </li>
                    {/* <li className={styles.layer__list}>
                            <p className={styles.layer__listTit}>권한</p>
                            <input className={styles.layer__listInput}
                                   type={"text"}
                                   name={"id"}
                                   value= {Array.isArray(data.auth) ? data.auth.join(", ") : data.auth}
                                   readOnly={true}/>
                        </li> */}

                    <li className={styles.layer__list}>
                        <p lang={"ko"} className={styles.layer__listTit}>권한</p>

                        {/* ✨ 1. authTagContainer의 내부 구조 변경 ✨ */}
                        <div className={modalStyles.authTagContainer}>
                            {/* 1-1. 선택된 태그들을 감싸는 별도의 div 추가 */}
                            <div className={modalStyles.tagList}>
                                {selectedAuths.map(authEngName => {
                                    const authObject = allAuthList.find(a => a.authorityEngName === authEngName); // allAuthList를 참조하도록 변경
                                    const isOwner = authEngName === 'TENANT_OWNER';
                                    const isLastOne = selectedAuths.length <= 1;

                                    return (
                                        // <div key={authEngName} className={`${modalStyles.authTag} ${isOwner ? modalStyles.ownerTag : ''}`}>
                                        // <div key={authEngName} className={`${modalStyles.authTag}`}>
                                        //     <span>{authObject ? authObject.authorityName : authEngName}</span>
                                        //     {(!isOwner && !isLastOne) && (
                                        //         <button onClick={() => handleRemoveAuth(authEngName)}>×</button>
                                        //     )}
                                        // </div>
                                        <InnocodeBadge
                                            theme={"line"}
                                            type={"assistive"}
                                            size={"md"}
                                        >
                                            {authObject ? authObject.authorityName : authEngName}
                                            {(!isOwner && !isLastOne) && (
                                                <button onClick={() => handleRemoveAuth(authEngName)}>×</button>
                                            )}
                                        </InnocodeBadge>
                                    );
                                })}
                            </div>

                            <div className={modalStyles.selectList}>
                                <InnocodeDropdown
                                    placeholder="권한 추가"
                                    value=""
                                    options={availableAuthsToSelect.map((auth) => ({
                                        value: auth.authorityEngName,
                                        label: auth.authorityName,
                                        disabled: false
                                    }))}
                                    onChange={(value) => {
                                        if (value && typeof value === 'string') {
                                            handleAddAuth(value);
                                        }
                                    }}
                                />
                            </div>


                        </div>
                        {/* 1-2. 권한 추가 드롭다운 (구조는 동일) */}


                        {/* { selectedAuth === 'TENANT_OWNER' ?
                                <input className={styles.layer__listInput}
                                   type={"text"}
                                   name={"authorityIds"}
                                   value= {selectedAuth}
                                   readOnly={true}/>
                                :

                            } */}


                    </li>
                </ul>


                <div className={modalStyles.serviceListSection}>
                    <p className={modalStyles.serviceListTitle}>가입 프로필</p>

                    <div className={modalStyles.profileCard}>
                        {/* 프로필 헤더: 이미지와 이름 */}
                        <div className={modalStyles.profileHeader}>
                            <img
                                src={data.imgUrl || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                alt="프로필 이미지"
                                className={modalStyles.profileImage}
                            />
                            <span className={modalStyles.profileName}>{data.name}</span>

                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '14px', color: serviceUser.status ? '#2e7d32' : '#d32f2f' }}>
                                    {serviceUser.status ? '활성' : '비활성'}
                                </span>
                                <Switch
                                    checked={serviceUser.status}
                                    onChange={handleToggleStatus}
                                    color={serviceUser.status ? "success" : "error"}
                                />
                            </div>
                        </div>

                        {/* 프로필 상세 정보: 가입일, 마지막 접속일 */}
                        <div className={modalStyles.profileDetails}>
                            <div className={modalStyles.detailRow}>
                                <span className={modalStyles.detailLabel}>가입일</span>
                                <span className={modalStyles.detailValue}>
                                    {/* dayjs를 사용하여 날짜 형식 변경. data.createdDate가 Unix 타임스탬프(숫자)라고 가정 */}
                                    {data.createdDate ? dayjs(data.createdDate).format('YYYY.MM.DD HH:mm') : '-'}
                                </span>
                            </div>
                            <div className={modalStyles.detailRow}>
                                <span className={modalStyles.detailLabel}>마지막 접속일</span>
                                <span className={modalStyles.detailValue}>
                                    {/* 0L 또는 0으로 오는 경우를 체크하여 '-'로 표시 */}
                                    {data.lastConnectionDate ? dayjs(data.lastConnectionDate).format('YYYY.MM.DD HH:mm:ss') : '기록 없음'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>


                <div className={styles.layer__sensorBtnBox}>
                    <InnocodeButton
                        size="md"
                        backgroundColor="var(--status-negative, #f83a3a)"
                        onClick={() => handleWithdrawal(serviceUser)}
                        fullWidth={true}
                    >
                        탈퇴
                    </InnocodeButton>
                    <InnocodeButton
                        size="md"
                        onClick={handleModify}
                        fullWidth={true}
                    >
                        수정
                    </InnocodeButton>
                </div>
            </div>
        </div>
    );
}
